import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  AfterViewInit,
  HostListener,
  OnDestroy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-signing-pad',
  standalone: true,
  template: `
    <div class="signature-container">
      <canvas
        #signPad
        class="signature-pad"
        (mousedown)="onMouseDown($event)"
        (mousemove)="onMouseMove($event)"
        (mouseup)="onMouseUp()"
        (mouseleave)="onMouseUp()"
        (touchstart)="onMouseDown($event)"
        (touchmove)="onMouseMove($event)"
        (touchend)="onMouseUp()"
      ></canvas>
      <div class="buttons">
        <button type="button" (click)="clearSignature()" [disabled]="disabled">Clear</button>
      </div>
    </div>
  `,
  styles: [
    `
      .signature-container {
        width: 100%;
        margin: 0 auto;
      }
      .signature-pad {
        width: 100%;
        height: 200px;
border: 1px solid var(--form-input-border, #ccc);
        touch-action: none;
      }
      .buttons {
        margin-top: 10px;
        display: flex;
        justify-content: center;
      }
      button {
        background-color: #0a7ea4;
        color: white;
        font-weight: 600;
        height: 35px;
        padding: 0 15px;
        border-radius: 3px;
        border: none;
      }
      button:disabled {
        background-color: #ccc;
        cursor: not-allowed;
      }
      @media (max-width: 768px) {
        .signature-pad {
          height: 150px;
        }
      }
      @media (max-width: 480px) {
        .signature-pad {
          height: 120px;
        }
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SigningPadComponent,
      multi: true,
    },
  ],
})
export class SigningPadComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @ViewChild('signPad', { static: false }) signPad!: ElementRef<HTMLCanvasElement>;
  @Input() signature: string | null = null;
  @Input() disabled: boolean = false;
  @Output() signatureChanged = new EventEmitter<string | null>();

  private context!: CanvasRenderingContext2D;
  private isDrawing = false;
  private onChange = (value: string | null) => {};
  private onTouched = () => {};
  private currentAspectRatio = 0; // Store aspect ratio to detect changes
  private resizeObserver: ResizeObserver | null = null;
  private originalSignatureData: ImageData | null = null; // Store original signature data
  private signatureScale = { x: 1, y: 1 }; // Track scaling factors

  ngAfterViewInit(): void {
    this.initializeCanvas();
    this.setupResizeObserver();
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private setupResizeObserver(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver((entries) => {
        // Handle resize with the observer
        this.handleCanvasResize();
      });
      this.resizeObserver.observe(this.signPad.nativeElement);
    }
  }

  private handleCanvasResize(): void {
    const savedSignature = this.signature;
    this.adjustCanvasResolution();
    if (savedSignature) {
      this.loadSignature(savedSignature);
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    if (!this.resizeObserver) {
      // Only use this if ResizeObserver isn't available
      this.handleCanvasResize();
    }
  }

  @HostListener('window:orientationchange', ['$event'])
  onOrientationChange(): void {
    // Handle orientation changes explicitly
    setTimeout(() => {
      this.handleCanvasResize();
    }, 200); // Small delay to ensure dimensions have updated
  }

  private initializeCanvas(): void {
    const canvas = this.signPad?.nativeElement;
    if (!canvas) {
      console.error("Canvas element is not available.");
      return;
    }

    this.context = canvas.getContext('2d', { willReadFrequently: true })!;
    if (!this.context) {
      console.error("Failed to get 2D context for canvas.");
      return;
    }

    this.context.strokeStyle = this.getStrokeColor();
    this.context.lineWidth = 2;
    this.context.lineCap = 'round';
    this.context.lineJoin = 'round';
    
    this.adjustCanvasResolution();
    
    if (this.signature) {
      this.loadSignature(this.signature);
    }
  }

  private getStrokeColor(): string {
    return document.body.classList.contains('dark-theme') ? '#ffffff' : '#000000';
  }

  adjustCanvasResolution(): void {
    const canvas = this.signPad.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const oldWidth = canvas.width;
    const oldHeight = canvas.height;
    const devicePixelRatio = window.devicePixelRatio || 1;
    
    // Capture current canvas content if it exists
    let imageData = null;
    if (oldWidth > 0 && oldHeight > 0) {
      try {
        imageData = this.context.getImageData(0, 0, oldWidth, oldHeight);
      } catch (e) {
        console.error("Failed to get image data:", e);
      }
    }
    
    // Set the canvas dimensions to match its CSS dimensions with proper pixel density
    canvas.width = rect.width * devicePixelRatio;
    canvas.height = rect.height * devicePixelRatio;
    
    // Apply device pixel ratio scaling
    this.context.scale(devicePixelRatio, devicePixelRatio);
    
    // Reset stroke styles after resize (canvas context resets after width/height change)
    this.context.strokeStyle = this.getStrokeColor();
    this.context.lineWidth = 2;
    this.context.lineCap = 'round';
    this.context.lineJoin = 'round';
    
    // If we have previous content, scale and redraw it
    if (imageData && oldWidth > 0 && oldHeight > 0) {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = rect.width;
      tempCanvas.height = rect.height;
      const tempContext = tempCanvas.getContext('2d')!;
      
      // Create a temporary canvas with the old image data
      const oldCanvas = document.createElement('canvas');
      oldCanvas.width = oldWidth;
      oldCanvas.height = oldHeight;
      const oldContext = oldCanvas.getContext('2d')!;
      oldContext.putImageData(imageData, 0, 0);
      
      // Draw the old canvas onto the new one with proper scaling
      tempContext.drawImage(oldCanvas, 0, 0, oldWidth, oldHeight, 0, 0, rect.width, rect.height);
      
      // Draw the scaled image onto our main canvas
      this.context.drawImage(tempCanvas, 0, 0);
    }
    
    // Calculate and store the new aspect ratio
    this.currentAspectRatio = rect.width / rect.height;
  }

  onMouseDown(event: any): void {
    if (this.disabled) return;
    event.preventDefault();
    
    this.context.strokeStyle = this.getStrokeColor();  // ADD THIS
    this.isDrawing = true;
    const coords = this.relativeCoords(event);
    this.context.beginPath();
    this.context.moveTo(coords.x, coords.y);
  }

  onMouseMove(event: any): void {
    if (!this.isDrawing || this.disabled) return;
    event.preventDefault();
    
    this.context.strokeStyle = this.getStrokeColor();  // ADD THIS
    const coords = this.relativeCoords(event);
    this.context.lineTo(coords.x, coords.y);
    this.context.stroke();
    this.updateSignature();
  }

  onMouseUp(): void {
    this.isDrawing = false;
    this.onTouched();
  }

  clearSignature(): void {
    if (!this.context || this.disabled) return;
    
    const canvas = this.signPad.nativeElement;
    this.context.clearRect(0, 0, canvas.width / (window.devicePixelRatio || 1), canvas.height / (window.devicePixelRatio || 1));
    this.context.beginPath();
    this.signature = null;
    this.originalSignatureData = null;
    this.signatureChanged.emit(null);
    this.onChange(null);
    this.onTouched();
  }

  private updateSignature(): void {
    if (this.disabled) return;
    
    const base64Image = this.signPad.nativeElement.toDataURL('image/png');
    this.signature = base64Image;
    
    // Store the original image data
    const canvas = this.signPad.nativeElement;
    const deviceRatio = window.devicePixelRatio || 1;
    this.originalSignatureData = this.context.getImageData(
      0, 0, 
      canvas.width / deviceRatio, 
      canvas.height / deviceRatio
    );
    
    this.signatureChanged.emit(base64Image);
    this.onChange(base64Image);
  }

  private relativeCoords(event: any): { x: number; y: number } {
    const canvas = this.signPad.nativeElement;
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;
    
    // Handle both mouse and touch events
    if (event.type.startsWith('touch')) {
      clientX = event.touches[0]?.clientX || event.changedTouches[0]?.clientX;
      clientY = event.touches[0]?.clientY || event.changedTouches[0]?.clientY;
    } else {
      clientX = event.clientX;
      clientY = event.clientY;
    }
    
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  }

  writeValue(signature: string | null): void {
    this.signature = signature;
    
    // Only attempt to load if context exists
    if (signature && this.context) {
      this.loadSignature(signature);
    } else if (this.context) {
      this.clearSignature();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private loadSignature(signature: string): void {
    const img = new Image();
    img.src = signature;
    img.onload = () => {
      const canvas = this.signPad.nativeElement;
      const deviceRatio = window.devicePixelRatio || 1;
      
      // Clear the canvas first
      this.context.clearRect(0, 0, canvas.width / deviceRatio, canvas.height / deviceRatio);
      
      // Calculate the best fit while maintaining aspect ratio
      const imgRatio = img.width / img.height;
      const canvasRatio = (canvas.width / deviceRatio) / (canvas.height / deviceRatio);
      
      let drawWidth, drawHeight, offsetX = 0, offsetY = 0;
      
      if (imgRatio > canvasRatio) {
        // Image is wider than canvas (relative to height)
        drawWidth = canvas.width / deviceRatio;
        drawHeight = drawWidth / imgRatio;
        offsetY = ((canvas.height / deviceRatio) - drawHeight) / 2;
      } else {
        // Image is taller than canvas (relative to width)
        drawHeight = canvas.height / deviceRatio;
        drawWidth = drawHeight * imgRatio;
        offsetX = ((canvas.width / deviceRatio) - drawWidth) / 2;
      }
      
      // Draw the image centered and scaled properly
      this.context.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
    };
  }
}